import type { MutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { bffUserProfileLoadedAtom, hasProfileAtom, isLoggedInAtom, selfUserLoadedAtom, userInfoAtom } from 'app/atoms';
import { asFormData } from 'app/lib/form';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import type { User } from 'app/types/user';
import type { AxiosError } from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';
import { http } from '../lib/axios';
import { getBffQueryData, useQueryLPC } from 'app/lib/query';
import { profileKeys } from './keys';
import { useQueryKeys } from './invalidation';
import type { Profile, CreateProfileDTO, UpdateProfileDTO, UpdateProfileUnavailabilityDTO } from 'app/types/profile';

export type { Profile, CreateProfileDTO, UpdateProfileDTO, UpdateProfileUnavailabilityDTO } from 'app/types/profile';

type ProfileQueryOptions = FunctionalUseQueryOptions<Profile, AxiosError> & {
  useBff?: boolean;
};

export function useProfile(userId?: User['id'], { useBff = true, ...opts }: ProfileQueryOptions = {}) {
  const client = useQueryClient();
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);
  const queryKey = useBff ? profileKeys.detail(userId!) : profileKeys.privateDetail(userId!);
  return useQueryLPC<Profile, AxiosError>({
    queryKey,
    queryFn: async () =>
      (useBff && bffUserProfileLoaded ? getBffQueryData<Profile>(client, queryKey) : undefined) ??
      (
        await http.get<Profile>(`/profiles/${userId!}`, {
          params: { typeOfUserId: typeof userId, bffUserProfileLoaded },
        })
      ).data,
    ...opts,
    enabled: Boolean(userId) && (opts.enabled ?? true) && (!useBff || bffUserProfileLoaded),
    retry: (failureCount, error) => {
      // 404 is "ok"; don't retry
      return failureCount < (error.response?.status === 404 ? 0 : 4);
    },
  });
}

export function useMyProfile(opts: FunctionalUseQueryOptions<Profile, AxiosError> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const hasProfile = useAtomValue(hasProfileAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const query = useProfile(userInfo?.id, {
    ...opts,
    enabled: isLoggedIn && !hasProfile && !!userInfo,
  });
  return query;
}

/** Fetches the authenticated user's complete profile, including private fields omitted from public/BFF DTOs. */
export function useMyPrivateProfile(opts: FunctionalUseQueryOptions<Profile, AxiosError> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const userInfo = useAtomValue(userInfoAtom);
  return useProfile(userInfo?.id, {
    ...opts,
    enabled: isLoggedIn && !!userInfo && (opts.enabled ?? true),
    useBff: false,
  });
}

export function useUpsertMyProfile(opts: MutationOptions<Profile, Error, CreateProfileDTO | UpdateProfileDTO> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);

  return useMutation<Profile, Error, CreateProfileDTO | UpdateProfileDTO>({
    mutationFn: (payload) => http.patch<Profile>('/profiles', asFormData(payload)).then((res) => res.data),

    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(profileKeys.detail(result.userId), result);
      client.setQueryData(profileKeys.privateDetail(result.userId), result);
      await qk.selfUser.invalidate();
      setSelfUserLoaded(false);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useUpdateMyProfileUnavailability(
  opts: MutationOptions<Profile, Error, UpdateProfileUnavailabilityDTO> = {},
) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);

  return useMutation<Profile, Error, UpdateProfileUnavailabilityDTO>({
    mutationFn: (payload) => http.patch<Profile>('/profiles/unavailability', payload).then((res) => res.data),

    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(profileKeys.detail(result.userId), result);
      client.setQueryData(profileKeys.privateDetail(result.userId), result);
      await qk.selfUser.invalidate();
      setSelfUserLoaded(false);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}

export function useDeleteMyProfileUnavailability(opts: MutationOptions<Profile> = {}) {
  const client = useQueryClient();
  const qk = useQueryKeys();
  const setSelfUserLoaded = useSetAtom(selfUserLoadedAtom);

  return useMutation<Profile>({
    mutationFn: () => http.delete<Profile>('/profiles/unavailability').then((res) => res.data),

    ...opts,
    async onSuccess(result, variables, context) {
      client.setQueryData(profileKeys.detail(result.userId), result);
      client.setQueryData(profileKeys.privateDetail(result.userId), result);
      await qk.selfUser.invalidate();
      setSelfUserLoaded(false);
      return opts.onSuccess?.(result, variables, context);
    },
  });
}
